/**
 * Hotfix: The Smart Menu (Qikify) app replaces the default Horizon megamenu,
 * which has been modified to include store-aware collection links.
 * This module patches the Smart Menu links to append the active store tag.
 */

import { STORE_PREFERENCE_EVENT, getActiveStore } from '@theme/store-routes';
import { onDocumentReady } from '@theme/utilities';

const MENU_ROOT_SELECTOR = 'header-component';
const MENU_LINK_SELECTOR = 'header-component a[href]';

/**
 * Normalize a value to a lowercase slug for comparison purposes only.
 * @param {string | null | undefined} value
 */
function normalizeForCompare(value) {
  return (value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim()
    .replace(/['']/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/**
 * Format a store name for use as a collection tag in URLs.
 * Returns the name exactly as provided by the store selector (e.g. "Nykøbing Falster", "Aarhus").
 * @param {string | null | undefined} value
 */
function toStoreTag(value) {
  return (value || '').trim().replace(/\s+/g, '-');
}

/** @param {string} activeSlugNormalized */
function getKnownStoreSlugs(activeSlugNormalized) {
  const slugs = new Set(activeSlugNormalized ? [activeSlugNormalized] : []);
  document.querySelectorAll('store-selector-drawer [data-location-name]').forEach((el) => {
    const slug = normalizeForCompare(el.getAttribute('data-location-name'));
    if (slug) slugs.add(slug);
  });
  return slugs;
}

/** @param {HTMLAnchorElement} anchor @param {string} storeTag @param {Set<string>} knownSlugs */
function rewriteLink(anchor, storeTag, knownSlugs) {
  const href = anchor.getAttribute('href');
  if (!href || href.startsWith('#') || href.startsWith('mailto:') || href.startsWith('tel:')) return;

  let url;
  try {
    url = new URL(href, window.location.origin);
  } catch {
    return;
  }

  if (url.origin !== window.location.origin) return;

  const parts = url.pathname.split('/').filter(Boolean);
  const idx = parts.indexOf('collections');
  if (idx === -1 || !parts[idx + 1] || parts.length > idx + 3) return;

  const tagSegment = parts[idx + 2];
  const existingTags = tagSegment ? decodeURIComponent(tagSegment).split('+').filter(Boolean) : [];
  const tags = existingTags.filter((t) => !knownSlugs.has(normalizeForCompare(t)));
  if (!tags.some((t) => normalizeForCompare(t) === normalizeForCompare(storeTag))) tags.push(storeTag);

  const newPath = `/${parts.slice(0, idx + 2).join('/')}${tags.length ? `/${tags.join('+')}` : ''}`;
  if (newPath === url.pathname) return;

  const suffix = url.search + url.hash;
  const nextHref = /^https?:\/\//i.test(href) ? `${url.origin}${newPath}${suffix}` : `${newPath}${suffix}`;
  anchor.setAttribute('href', nextHref);
}

function rewriteAll() {
  const storeName = getActiveStore()?.name;
  const storeTag = toStoreTag(storeName);
  const normalized = normalizeForCompare(storeName);
  if (!storeTag || !document.querySelector(MENU_ROOT_SELECTOR)) return;

  const knownSlugs = getKnownStoreSlugs(normalized);
  document.querySelectorAll(MENU_LINK_SELECTOR).forEach((a) => {
    if (a instanceof HTMLAnchorElement) rewriteLink(a, storeTag, knownSlugs);
  });
}

let queued = false;
function scheduleRewrite() {
  if (queued) return;
  queued = true;
  requestAnimationFrame(() => {
    queued = false;
    rewriteAll();
  });
}

onDocumentReady(() => {
  scheduleRewrite();

  // Re-run when the 3rd-party smart menu injects or updates DOM
  new MutationObserver((mutations) => {
    for (const m of mutations) {
      for (const node of m.addedNodes) {
        if (node instanceof Element && (node.matches?.(MENU_ROOT_SELECTOR) || node.closest?.(MENU_ROOT_SELECTOR) || node.querySelector(MENU_ROOT_SELECTOR))) {
          scheduleRewrite();
          return;
        }
      }
    }
  }).observe(document.body, { childList: true, subtree: true });

  // Re-run on hover/focus so lazily-rendered submenus get rewritten
  document.addEventListener(
    'pointerover',
    (e) => {
      if (e.target instanceof Element && e.target.closest('.tmenu_item_link')) scheduleRewrite();
    },
    true,
  );

  // Intercept clicks — the smart menu app may navigate with its own cached URLs,
  // bypassing our rewritten href. We capture the click early and navigate ourselves.
  document.addEventListener(
    'click',
    (e) => {
      const anchor = e.target instanceof Element ? e.target.closest(MENU_LINK_SELECTOR) : null;
      if (!(anchor instanceof HTMLAnchorElement)) return;

      const href = anchor.getAttribute('href');
      if (!href || !href.includes('/collections/')) return;

      e.preventDefault();
      e.stopImmediatePropagation();
      window.location.assign(href);
    },
    true,
  );

  document.addEventListener('smartmenu-loaded', scheduleRewrite);
  document.addEventListener(STORE_PREFERENCE_EVENT, scheduleRewrite);
});
